import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Form, Button, Typography, Space, Radio, Input, Rate, Progress, message, Skeleton } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { BaseUrl } from '../Constants/Constants';
import axios from 'axios';

const { Title, Paragraph, Text } = Typography;

/**
 * Props
 * - visible: controls modal visibility
 * - courseId: course id to fetch forms
 * - userId: current user id
 * - onSuccessRedirect: callback to execute after successful submit (e.g., navigate)
 */
const FeedbackModal = ({ visible, courseId, userId, onSuccessRedirect }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [forms, setForms] = useState([]);
    const [activeFormIndex, setActiveFormIndex] = useState(0);

    const activeForm = useMemo(() => forms?.[activeFormIndex], [forms, activeFormIndex]);

    const answeredCount = useMemo(() => {
        if (!activeForm) return 0;
        const values = form.getFieldsValue();
        return (activeForm.questions || []).filter(q => values[q.id] !== undefined && values[q.id] !== '' && values[q.id] !== null).length;
    }, [activeForm, form]);

    const totalRequired = useMemo(() => {
        if (!activeForm) return 0;
        return (activeForm.questions || []).filter(q => q.required).length;
    }, [activeForm]);

    const percent = useMemo(() => {
        if (!activeForm) return 0;
        const total = (activeForm.questions || []).length || 1;
        return Math.round((answeredCount / total) * 100);
    }, [activeForm, answeredCount]);

    useEffect(() => {
        if (!visible || !courseId) return;
        setLoading(true);
        axios.post(`${BaseUrl}feedback-form/list_of_feedback_forms`, 
            { course_id: courseId },
            { withCredentials: true }
        )
            .then(res => {
                const data = res.data;
                if (data?.success === false) {
                    throw new Error(data?.message || 'Failed to fetch feedback form');
                }
                return data;
            })
            .then(data => {
                const list = Array.isArray(data?.data) ? data.data : [];
                
                // Transform the API response to match the expected format
                const transformedForms = list.map(form => ({
                    id: form.feedback_form_id,
                    title: form.form_title,
                    description: form.form_description || '',
                    course_id: form.course_id,
                    questions: JSON.parse(form.form_questions || '[]').map(q => ({
                        id: q.id,
                        question: q.text,
                        type: q.type,
                        required: q.required,
                        order: q.order,
                        options: q.options || [],
                        placeholder: q.placeholder || '',
                        min: q.min,
                        max: q.max
                    }))
                }));
                
                setForms(transformedForms);
                setActiveFormIndex(0);
                form.resetFields();
            })
            .catch(err => {
                message.error(err.message || 'Unable to load feedback form');
            })
            .finally(() => setLoading(false));
    }, [visible, courseId, form]);

    useEffect(() => {
        // Reset answers when active form changes
        form.resetFields();
    }, [activeFormIndex]);

    const renderQuestion = (q) => {
        if (!q) return null;
        const rules = q.required ? [{ required: true, message: 'This question is required' }] : [];
        switch (q.type) {
            case 'rating': {
                const min = Number.isFinite(q.min) ? q.min : 1;
                const max = Number.isFinite(q.max) ? q.max : 5;
                return (
                    <Form.Item key={q.id} name={q.id} label={q.question} rules={rules} valuePropName="value">
                        <Rate count={max} allowClear={!q.required} />
                    </Form.Item>
                );
            }
            case 'yes_no': {
                return (
                    <Form.Item key={q.id} name={q.id} label={q.question} rules={rules}>
                        <Radio.Group>
                            <Radio value={true}>Yes</Radio>
                            <Radio value={false}>No</Radio>
                        </Radio.Group>
                    </Form.Item>
                );
            }
            case 'multiple_choice': {
                return (
                    <Form.Item key={q.id} name={q.id} label={q.question} rules={rules}>
                        <Radio.Group>
                            {q.options?.map((option, index) => (
                                <Radio key={index} value={option}>{option}</Radio>
                            ))}
                        </Radio.Group>
                    </Form.Item>
                );
            }
            case 'text':
            default: {
                return (
                    <Form.Item key={q.id} name={q.id} label={q.question} rules={rules}>
                        <Input.TextArea placeholder={q.placeholder || 'Type your answer'} autoSize={{ minRows: 2, maxRows: 6 }} />
                    </Form.Item>
                );
            }
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            if (!activeForm) {
                message.error('No feedback form available');
                return;
            }
            const payload = {
                feedback_form_id: activeForm.id,
                course_id: activeForm.course_id || courseId,
                user_id: userId,
                answers: values,
                submitted_at: new Date().toISOString()
            };
            setSubmitting(true);
            const res = await axios.post(`${BaseUrl}feedback-form/submit_feedback`, payload, {
                withCredentials: true
            });
            const data = res.data;
            if (data?.success === false) {
                throw new Error(data?.message || 'Failed to submit feedback');
            }
            message.success(data?.message || 'Feedback submitted successfully');
            if (onSuccessRedirect) onSuccessRedirect();
        } catch (err) {
            if (err?.errorFields) {
                message.warning('Please answer all required questions');
            } else {
                message.error(err.message || 'Submission failed');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const footer = (
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Space>
                <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                <Text type="secondary">Feedback is mandatory to continue</Text>
            </Space>
            <Button type="primary" onClick={handleSubmit} loading={submitting} disabled={!activeForm}>
                Submit feedback
            </Button>
        </Space>
    );

    return (
        <Modal
            open={visible}
            onCancel={() => {}}
            maskClosable={false}
            closable={false}
            footer={footer}
            width={720}
            style={{ top: 24 }}
            bodyStyle={{ paddingTop: 8, paddingBottom: 8 }}
            destroyOnClose
        >
            {loading ? (
                <Skeleton active paragraph={{ rows: 6 }} />
            ) : !activeForm ? (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Title level={4}>No feedback form available</Title>
                    <Paragraph type="secondary">Please contact support if this persists.</Paragraph>
                </Space>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        <Title level={4} style={{ marginBottom: 0 }}>{activeForm.title || 'Course Feedback'}</Title>
                        {activeForm.description ? (
                            <Paragraph type="secondary" style={{ marginTop: 0 }}>{activeForm.description}</Paragraph>
                        ) : null}
                        <Progress percent={percent} size="small" />
                    </Space>

                    <Form
                        form={form}
                        layout="vertical"
                        onValuesChange={() => { /* trigger progress recompute */ }}
                    >
                        {(activeForm.questions || []).map(renderQuestion)}
                    </Form>

                    {forms.length > 1 ? (
                        <Space style={{ justifyContent: 'center' }}>
                            <Button disabled={activeFormIndex === 0} onClick={() => setActiveFormIndex(v => Math.max(0, v - 1))}>Previous</Button>
                            <Button disabled={activeFormIndex === forms.length - 1} onClick={() => setActiveFormIndex(v => Math.min(forms.length - 1, v + 1))}>Next</Button>
                        </Space>
                    ) : null}
                </div>
            )}
        </Modal>
    );
};

export default FeedbackModal;



